import React, { useCallback } from 'react';
import { Modal } from '../../../Modal';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { EditPhoneForm } from './EditPhoneForm.component';
import { ContactName } from '@uplift/api';
import { VerificationForm } from './VerificationForm.component';
import { useInitiateContactVerification } from './PhoneVerificationModal.hooks';
import { useAlert } from 'react-alert';


export type PhoneVerificationModalProps = {
  contactId: string;
  contactName: ContactName;
  open: boolean;
  phone: string;
  userId: string;
  disableEdit?: boolean;
  maskPhone?: boolean;
  successText?: string;
  title?: string;
  onClose?: () => void;
  onSubmit?: () => void;
};

export const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = (props) => {
  const isMobile = useIsMobile();
  const alert = useAlert();
  const l10n = useL10n();
  const [ state, setState ] = React.useState<'verification' | 'phoneEdit'>('verification');


  const {
    initiateContactVerificationLoading,
    initiateContactVerification
  } = useInitiateContactVerification();

  const sendNewCode = useCallback(async (contactId: string, withAlert?: boolean) => {
    await initiateContactVerification({ contactId: contactId });

    if (withAlert) {
      alert.success(<strong>{l10n.phoneValidationModal.newCodeSent}</strong>);
    }
  }, [ alert, initiateContactVerification, l10n.phoneValidationModal.newCodeSent ]);

  const contactIdRef = React.useRef(props.contactId);

  React.useEffect(() => {
    if (!props.open || contactIdRef.current === props.contactId) {
      return;
    }

    sendNewCode(props.contactId, contactIdRef.current !== props.contactId);
    contactIdRef.current = props.contactId;
  }, [ props.open, props.contactId ]);

  const handleEditClick = useCallback(() => {
    if (props.disableEdit) {
      return undefined;
    }

    setState('phoneEdit');
  }, [ props.disableEdit ]);

  const currentModalContent = React.useMemo(() => {
    if (state === 'phoneEdit') {
      return (
        <EditPhoneForm
          open={props.open}
          userId={props.userId}
          phone={props.phone}
          contactName={props.contactName}
          onClose={() => setState('verification')}
        />
      );
    }

    return (
      <VerificationForm
        open={props.open}
        onClose={props.onClose}
        onSubmit={props.onSubmit}
        contactId={props.contactId}
        loading={initiateContactVerificationLoading}
        disableEdit={props.disableEdit}
        maskPhone={props.maskPhone}
        phone={props.phone}
        title={props.title}
        onSendNewCode={() => sendNewCode(props.contactId, true)}
        onEditMobileNumber={handleEditClick}
        successText={props.successText}
      />
    );
  }, [ state, props.open, props.onClose, props.onSubmit, props.contactId, props.disableEdit, props.maskPhone, props.phone, props.title, props.successText, props.userId, props.contactName, initiateContactVerificationLoading, handleEditClick, sendNewCode ]);

  const handleClose = React.useMemo(() => {
    if (state === 'phoneEdit') {
      return () => setState('verification');
    }

    return props.onClose;
  }, [ state, props.onClose ]);

  return (
    <Modal
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      open={props.open}
      onClose={handleClose}
      TransitionProps={{
        onExited() {
          setState('verification');
        },
      }}
    >
      {currentModalContent}
    </Modal>
  );
};
